import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import {
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import { UtilService } from '@service/util/util.service';
import {
	ADD_DAYS,
	CALCULATE_TRANSIT_DAYS,
	CustomerDTO,
	DATE_MDY,
	DATE_TIME,
	DATE_YMD,
	DIFFERENCE_IN_DAYS,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	MyLoadsTable,
	Quote,
	RestService,
	TODAY,
	IS_NULL_OR_UNDEFINED,
	IS_EMPTY,
	TO_CENTS,
	TO_DOLLARS,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { TRAILER_LENGTHS } from '@domain/constants/trailer-lengths';
import { finalize } from 'rxjs/operators';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { environment } from '@environment/environment';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';

@Component({
	selector: 'app-load-details-template',
	templateUrl: './load-details-template.component.html',
	styleUrls: ['./load-details-template.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class LoadDetailsTemplateComponent implements OnInit {
	isCreatingLoad: boolean = false;

	@Input() postLoad: boolean = false;
	@Input() data: any;
	@Input() isDuplicateLoad: boolean = false;
	@Input() load: MyLoadsTable = null;
	@Input() isEditQuote: boolean = false;
	@Input() quote: Quote = null;
	@Input() excludedCarrierIds: Array<any>;
	@Input() oldLoadDetails: UntypedFormGroup;
	@Output() searchTruck = new EventEmitter<string>();
	@Output() loadCreated = new EventEmitter<string>();
	@Output() switchToCarrierFilter: EventEmitter<any> = new EventEmitter<String>();
	isFormInitialized = false;
	carrierCountForEmail = 0;

	loadDetails: UntypedFormGroup;

	estimatedDeliveryDate = new UntypedFormControl(new Date(ADD_DAYS(DATE_TIME(new Date()), 1)));
	pickupDate = new UntypedFormControl(new Date());
	minimumPickupDate = new Date();
	minDeliveryDate = new Date();
	teamService = false;
	refeerTemperature = '';
	temperatureModel = { continuous: 32, range: { min: -35, max: 20 } };
	tarpingModel = {
		tarping: false,
		specifications: [
			{ model: false, label: '4 Foot', value: 'FourFoot' },
			{ model: false, label: '6 Foot', value: 'SixFoot' },
			{ model: false, label: '8 Foot', value: 'EightFoot' },
		],
	};
	TRAILER_LENGTHS = TRAILER_LENGTHS;

	postLoadData = {};

	model = {
		emailByTruckIds: null,
		demoLoad: null,
		quote: {
			accessorials: [],
			declaredValue: null,
			deliveryCloseTime: '00:00',
			deliveryOpenTime: '00:00',
			desiredPrice: null,
			destination: null,
			estimatedDeliveryDate: null,
			origin: null,
			pickupCloseTime: '00:00',
			pickupDate: null,
			pickupOpenTime: '00:00',
			productDescription: null,
			referenceNumber: null,
			specialInstructions: null,
			tarping: null,
			temperatureRange: {},
			totalWeight: null,
			trailerType: null,
			trailerLength: null,
			customerCode: null,
			predictedPrice: null,
		},
		truckSearchId: null,
		truckLaneSearchId: null,

		// bad variable name in c4 code. need to be changed to matchedCarrierIds later.
		matchedCarriers: [],
	};
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	transitDays = null;
	pickupDateLeadTimeData = {
		isBeyondLimit: false,
		errorMessage: '',
	};
	bookingAssistantSettings = null;
	selectedCustomer: CustomerDTO = null;
	baConfig = {
		isLoadPricingSupported: false,
		primaryPricingField: null,
		secondaryPricingField: null,
	};
	showCustomerDropdown: boolean = true;

	@ViewChild('pdes') productDescriptionInput: ElementRef<HTMLElement>;

	constructor(
		private formBuilder: UntypedFormBuilder,
		public utilService: UtilService,
		private router: Router,
		private urlService: UrlService,
		private restService: RestService,
		private segmentWrapper: SegmentWrapperService,
		public accountSettingsService: AccountSettingsService
	) {
		this.model.demoLoad = this.accountSettingsService.DEMO_ACCOUNT;
		this.bookingAssistantSettings = this.accountSettingsService.BOOKING_ASSISTANT_SETTINGS;
		this.pickupDateLeadTimeData.errorMessage = `You pickup date lead time
		is set to ${this.bookingAssistantSettings.pickupDateThreshold} days. This load
		will not be sent out.`;
	}

	ngOnInit() {
		const openTime: string =
			!this.postLoad && DATE_YMD(this.data.searchTruckForm.pickupDate) === DATE_YMD(new Date())
				? this.getOpenTime()
				: '08:00';
		this.loadDetails = this.oldLoadDetails
			? this.oldLoadDetails
			: this.formBuilder.group({
					referenceNumber: ['', Validators.required],
					productDescription: ['', Validators.required],
					totalWeight: null,
					desiredPrice: null,
					declaredValue: null,
					origin: null,
					destination: null,
					trailerType: [!this.postLoad ? this.data.searchTruckForm.trailerType : ''],
					pickupDate: [''],
					deliveryCloseTime: ['16:00', Validators.required],
					deliveryOpenTime: ['08:00', Validators.required],
					pickupCloseTime: ['16:00', Validators.required],
					pickupOpenTime: [openTime, Validators.required],
					pickupAppointment: this.accountSettingsService.PICK_UP_APPOINTMENT_REQUIRED,
					deliveryAppointment: this.accountSettingsService.DELIVERY_APPOINTMENT_REQUIRED,
					teamService: false,
					specialInstructions: '',
					selectedTarping: null,
					trailerLength: '53',
				});

		if (this.accountSettingsService.DECLARED_VALUE_REQUIRED) {
			this.loadDetails.get('declaredValue').setValidators([Validators.required]);
		}

		if (this.accountSettingsService.WEIGHT_REQUIRED) {
			this.loadDetails.get('totalWeight').setValidators([Validators.required]);
		}

		if (!this.accountSettingsService.SEND_LOADS_WITH_NO_RATE) {
			this.loadDetails.get('desiredPrice').setValidators([Validators.required]);
		}

		if (this.postLoad) {
			this.loadDetails.get('origin').setValidators([Validators.required]);
			this.loadDetails.get('destination').setValidators([Validators.required]);
			this.loadDetails.get('trailerType').setValidators([Validators.required]);
		} else {
			this.pickupDate = new UntypedFormControl(
				new Date(DATE_TIME(this.data.searchTruckForm.pickupDateStart))
			);
			this.estimatedDeliveryDate = new UntypedFormControl(
				new Date(DATE_TIME(ADD_DAYS(this.data.searchTruckForm.pickupDateStart, 1)))
			);
			this.getDistance();
		}
		this.subscribeToTimeChange();
		this.updatePickupTime();
		this.checkPickupLeadTime();
		if (this.isDuplicateLoad) {
			this.setDupilcateLoadData();
		}

		if (this.isEditQuote) {
			this.setEditQuoteFormFields();
		}
		// this.setOfferedPrice();
		this.isFormInitialized = true;

		if (this.excludedCarrierIds) {
			this.carrierCountForEmail =
				this.data.inNetworkCarrierCountForSearch - (this.excludedCarrierIds?.length || 0);
		} else {
			this.carrierCountForEmail = this.data?.inNetworkCarrierCountForSearch || 0;
		}

		this.getPricingFieldFromBAConfig();
		if (
			!this.utilService.isUserRoleAdmin() &&
			this.accountSettingsService.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS
		) {
			this.showCustomerDropdown = false;
		}
	}

	setDupilcateLoadData() {
		this.loadDetails.get('destination').setValue(this.load.destinationDTO);
		this.loadDetails.get('origin').setValue(this.load.originDTO);
		this.loadDetails.get('trailerType').setValue(this.load.trailerType.toUpperCase());
		this.loadDetails.get('referenceNumber').setValue(this.load.referenceNumber);
		this.loadDetails.get('productDescription').setValue(this.load.productDescription);
		this.loadDetails.get('declaredValue').setValue(Number(this.load.declaredValue) / 100);
		this.loadDetails.get('desiredPrice').setValue(Number(this.load.price) / 100);
		this.loadDetails.get('teamService').setValue(this.load.teamServiceRequired);
		const transitDays: number = CALCULATE_TRANSIT_DAYS(
			this.load.pickupDate,
			this.load.deliveryDate
		);
		this.estimatedDeliveryDate = new UntypedFormControl(
			new Date(DATE_TIME(ADD_DAYS(new Date(), transitDays)))
		);
		this.loadDetails.get('totalWeight').setValue(this.load.totalWeight);
	}

	setEditQuoteFormFields() {
		this.loadDetails.get('destination').setValue(this.quote.destination);
		this.loadDetails.get('origin').setValue(this.quote.origin);
		this.loadDetails.get('trailerType').setValue(this.quote.equipment.toUpperCase());
		this.loadDetails.get('referenceNumber').setValue(this.quote.referenceNumber);
		this.loadDetails.get('productDescription').setValue(this.quote.productDescription);
		// TO_DOLLARS passes null/undefined through untouched. Number(undefined) / 100 gave NaN, which
		// survived the != null guard in buildQuoteRequest and serialized to null.
		this.loadDetails.get('declaredValue').setValue(TO_DOLLARS(this.quote.declaredValue));
		this.loadDetails.get('desiredPrice').setValue(Number(this.quote.desiredPrice) / 100);
		this.loadDetails.get('teamService').setValue(this.quote.teamServiceRequired);
		this.loadDetails.get('pickupAppointment').setValue(this.quote.pickupAppointmentRequired);
		this.loadDetails.get('deliveryAppointment').setValue(this.quote.deliveryAppointmentRequired);
		this.pickupDate = new UntypedFormControl({
			value: new Date(DATE_MDY(this.quote.pickupDate)),
			disabled: false,
		});
		this.estimatedDeliveryDate = new UntypedFormControl({
			value: new Date(DATE_MDY(this.quote.estimatedDeliveryDate)),
			disabled: false,
		});
		if (IS_NOT_NULL_OR_UNDEFINED(this.quote.pickupOpenTime)) {
			this.loadDetails.get('pickupOpenTime').setValue(this.quote.pickupOpenTime.substring(0, 5));
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.quote.pickupCloseTime)) {
			this.loadDetails.get('pickupCloseTime').setValue(this.quote.pickupCloseTime.substring(0, 5));
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.quote.deliveryOpenTime)) {
			this.loadDetails
				.get('deliveryOpenTime')
				.setValue(this.quote.deliveryOpenTime.substring(0, 5));
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.quote.deliveryCloseTime)) {
			this.loadDetails
				.get('deliveryCloseTime')
				.setValue(this.quote.deliveryCloseTime.substring(0, 5));
		}
		this.loadDetails.get('totalWeight').setValue(this.quote.totalWeight);
		this.loadDetails.get('specialInstructions').setValue(this.quote.specialInstructions);

		this.loadDetails.controls['referenceNumber'].disable();
		this.transitDays = CALCULATE_TRANSIT_DAYS(
			this.quote.pickupDate,
			this.quote.estimatedDeliveryDate
		);

		setTimeout(() => {
			if (
				IS_NOT_NULL_OR_UNDEFINED(this.productDescriptionInput) &&
				!this.utilService.isNullOrUndefined(this.productDescriptionInput.nativeElement)
			) {
				this.productDescriptionInput.nativeElement.blur();
			}
		}, 500);
	}

	setOfferedPrice() {
		if (
			!this.utilService.isNullOrUndefined(this.data) &&
			!this.utilService.isNullOrUndefined(this.data.lanePricing) &&
			!this.utilService.isNullOrUndefined(this.data.lanePricing.allInCost)
		) {
			this.loadDetails.get('desiredPrice').setValue(Math.round(this.data.lanePricing.allInCost));
		}
	}

	subscribeToTimeChange() {
		this.loadDetails.get('pickupOpenTime').valueChanges.subscribe((changedTime: string) => {
			this.openTimeChanged('pickup', changedTime);
		});
		this.loadDetails.get('deliveryOpenTime').valueChanges.subscribe((changedTime: string) => {
			this.openTimeChanged('delivery', changedTime);
		});
	}

	syncSearchData(searchFormData) {
		const data = this.utilService.getTruckSearchObject(searchFormData);
		this.loadDetails.get('origin').setValue(data.origin);
		this.loadDetails.get('destination').setValue(data.destination);
		this.loadDetails.get('trailerType').setValue(data.trailerType);
	}

	onSubmit() {
		if (this.loadDetails.valid) {
			this.isCreatingLoad = true;
			this.buildQuoteRequest();
			const url = this.urlService.createQuote();
			let event, segmentEventPayload;
			if (this.postLoad) {
				event = SegmentEventsConstants.user_posted_load;
				segmentEventPayload = this.postLoadData;
			} else {
				event = SegmentEventsConstants.user_submitted_booking_assistant;
				segmentEventPayload = this.model;
			}
			this.segmentWrapper.fireSegmentEvent(event, segmentEventPayload);
			this.restService
				.POST(url, this.postLoad ? this.postLoadData : this.model)
				.pipe(
					finalize(() => {
						this.isCreatingLoad = false;
					})
				)
				.subscribe(
					(result) => {
						if (result.data != null) {
							this.utilService.showSnackbar(
								'Your load  has been created and C4 is working to find carriers.',
								SNACKBAR.SUCCESS,
								SNACKBAR.ACTION
							);
							this.router.navigate(['/users/load_details', result.data.id], {
								queryParams: { quote: 'true' },
							});
							this.loadCreated.emit(null);
						} else {
							this.utilService.showSnackbar(
								'Unable to create quote now. Please try again later.',
								SNACKBAR.WARNING,
								SNACKBAR.ACTION
							);
						}
					},
					(error) => {
						let errorMsg = 'Unable to create quote at this time.';
						if (IS_NOT_EMPTY(error.error)) {
							errorMsg = error.error;
						}
						this.utilService.showSnackbar(errorMsg, SNACKBAR.ERROR, SNACKBAR.ACTION);
					}
				);
		}
	}

	buildQuoteRequest() {
		this.model.quote.estimatedDeliveryDate = DATE_YMD(this.estimatedDeliveryDate.value);
		this.model.quote.accessorials = [];
		if (this.loadDetails.controls['deliveryAppointment'].value) {
			this.model.quote.accessorials.push('DELIVERY_APPOINTMENT');
		}
		if (this.loadDetails.controls['pickupAppointment'].value) {
			this.model.quote.accessorials.push('PICKUP_APPOINTMENT');
		}
		if (this.loadDetails.controls['teamService'].value) {
			this.model.quote.accessorials.push('TEAM_SERVICE');
		}
		if (this.refeerTemperature == 'range') {
			this.model.quote.temperatureRange = {
				type: 'Range',
				minTemp: this.convertToCelsius(this.temperatureModel.range.min),
				maxTemp: this.convertToCelsius(this.temperatureModel.range.max),
			};
		}
		if (this.refeerTemperature == 'continous') {
			this.model.quote.temperatureRange = {
				type: 'Continuous',
				minTemp: this.convertToCelsius(this.temperatureModel.continuous),
				maxTemp: null,
			};
		}
		this.model.quote.deliveryCloseTime = this.loadDetails.controls[
			'deliveryCloseTime'
		].value.substring(0, 5);
		this.model.quote.deliveryOpenTime = this.loadDetails.controls[
			'deliveryOpenTime'
		].value.substring(0, 5);
		this.model.quote.pickupCloseTime = this.loadDetails.controls['pickupCloseTime'].value.substring(
			0,
			5
		);
		this.model.quote.pickupOpenTime = this.loadDetails.controls['pickupOpenTime'].value.substring(
			0,
			5
		);
		this.model.quote.specialInstructions = this.loadDetails.controls['specialInstructions'].value;
		this.model.quote.referenceNumber = this.loadDetails.controls['referenceNumber'].value;
		this.model.quote.productDescription = this.loadDetails.controls['productDescription'].value;
		this.model.quote.desiredPrice = this.loadDetails.controls['desiredPrice'].value * 100;
		// On edit, a field the broker never supplied is left out of the payload so the server keeps
		// the stored value - the declared value and weight inputs are hidden on TMS-fed accounts and
		// must not be wiped. On create there is no stored value to keep, so the 0 / '0' defaults stay.
		const declaredValue = this.loadDetails.controls['declaredValue'].value;
		const totalWeight = this.loadDetails.controls['totalWeight'].value;
		this.model.quote.declaredValue = this.hasNumericValue(declaredValue)
			? declaredValue * 100
			: this.isEditQuote
				? undefined
				: 0;
		this.model.quote.totalWeight = this.hasNumericValue(totalWeight)
			? totalWeight.toString()
			: this.isEditQuote
				? undefined
				: '0';

		if (this.loadDetails.get('trailerType').value == 'FLATBED') {
			this.model.quote.trailerLength = this.loadDetails.controls['trailerLength'].value;
		}
		this.model.quote.customerCode = IS_NOT_NULL_OR_UNDEFINED(this.selectedCustomer)
			? IS_NOT_NULL_OR_UNDEFINED(this.selectedCustomer.customerCode)
				? this.selectedCustomer.customerCode
				: this.selectedCustomer.name
			: null;
		if (this.postLoad) {
			this.model.quote.origin = this.loadDetails.get('origin').value;
			this.model.quote.destination = this.loadDetails.get('destination').value;
			this.model.quote.trailerType = this.formatPowerOnlyTrailerType(
				this.loadDetails.get('trailerType').value.toUpperCase()
			);
			this.model.quote.pickupDate = DATE_YMD(this.pickupDate['value']);
			if (this.loadDetails.get('trailerType').value == 'FLATBED' && this.tarpingModel.tarping) {
				this.model.quote.tarping = {
					required: true,
					size: this.loadDetails.controls['selectedTarping'].value,
				};
			}
			this.postLoadData['destination'] = this.loadDetails.get('destination').value;
			this.postLoadData['origin'] = this.loadDetails.get('origin').value;
			this.postLoadData['pickupDate'] = DATE_YMD(this.pickupDate['value']);
			this.postLoadData['quote'] = Object.assign({}, this.model.quote);
			this.postLoadData['trailerType'] = this.loadDetails.get('trailerType').value.toUpperCase();
			this.postLoadData['demoLoad'] = this.model.demoLoad;
		} else {
			this.model.emailByTruckIds = this.data.emailByTruckIds;
			this.model.truckSearchId = this.data.truckSearchIds;
			this.model.truckLaneSearchId = this.data.truckLaneSearchId;
			this.model.quote.origin = this.data.searchTruckForm.origin;
			this.model.quote.destination = this.data.searchTruckForm.destination;

			const trailerType = this.formatPowerOnlyTrailerType(
				this.data.searchTruckForm.trailerType.split(',')[0].toUpperCase()
			);
			this.model.quote.trailerType = trailerType;
			this.model.quote.pickupDate = DATE_YMD(this.pickupDate['value']);
			if (this.data.searchTruckForm.trailerType == 'FLATBED' && this.tarpingModel.tarping) {
				this.model.quote.tarping = {
					required: true,
					size: this.loadDetails.controls['selectedTarping'].value,
				};
			}
		}
		let predictedPrice = this.data?.lanePricing?.allInCost;
		if (predictedPrice) {
			predictedPrice = TO_CENTS(predictedPrice);
		}
		this.model.quote.predictedPrice = predictedPrice;
	}

	convertToCelsius(temp) {
		return ((temp - 32) * 5) / 9;
	}

	formatSliderLabel(value: number | null) {
		if (!value) {
			return 0;
		}
		return value + 'F';
	}

	openTimeChanged(type, timeValue: string) {
		let time,
			selected = timeValue.split(':');
		time = Number(selected[0]) + 1;
		time = time >= 24 ? time % 24 : time;
		time = time.toString().length == 1 ? '0' + time : time;
		time += ':' + selected[1];
		if (type == 'pickup') {
			this.loadDetails.controls['pickupCloseTime'].setValue(time);
		} else {
			this.loadDetails.controls['deliveryCloseTime'].setValue(time);
		}
	}

	getOpenTime(): string {
		const currentDate = new Date();
		const currentHour = (currentDate.getHours() + 1).toString();
		const currentMins = '00';
		return (currentHour.length == 1 ? '0' + currentHour : currentHour) + ':' + currentMins;
	}

	pickupDateChanged(dateObj) {
		if (!this.utilService.isNullOrUndefined(this.transitDays)) {
			this.estimatedDeliveryDate = new UntypedFormControl(
				new Date(ADD_DAYS(dateObj.value, this.transitDays))
			);
		} else {
			this.estimatedDeliveryDate = new UntypedFormControl(new Date(DATE_TIME(dateObj.value)));
		}
		this.minDeliveryDate = new Date(dateObj.value);
		this.updatePickupTime();
		this.checkPickupLeadTime();
	}

	updateQuote() {
		if (this.loadDetails.valid) {
			this.isCreatingLoad = true;
			this.buildQuoteRequest();
			const url: string =
				this.urlService.updateQuoteUrl(this.quote.id) + '?isSendEmail=' + this.isSendUpdateEmail();
			this.restService.PUT(url, this.model).subscribe(
				(result) => {
					this.utilService.showSnackbar(
						'Your quote  has been updated.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					this.loadCreated.emit(result);
				},
				(error) => {
					let errorMsg = 'Unable to update quote now. Please try again later.';
					if (IS_NOT_EMPTY(error.error)) {
						errorMsg = error.error;
					}
					this.utilService.showSnackbar(errorMsg, SNACKBAR.ERROR, SNACKBAR.ACTION);
					this.isCreatingLoad = false;
				}
			);
		}
	}

	isSendUpdateEmail(): string {
		if (
			this.quote.pickupOpenTime == this.model.quote.pickupOpenTime &&
			this.quote.pickupCloseTime == this.model.quote.pickupCloseTime &&
			this.quote.deliveryOpenTime == this.model.quote.deliveryOpenTime &&
			this.quote.deliveryCloseTime == this.model.quote.deliveryCloseTime &&
			this.quote.desiredPrice == this.model.quote.desiredPrice &&
			this.quote.totalWeight == this.model.quote.totalWeight &&
			this.quote.productDescription == this.model.quote.productDescription &&
			this.accountSettingsService.PICK_UP_APPOINTMENT_REQUIRED ==
				JSON.parse(this.loadDetails.controls['pickupAppointment'].value) &&
			this.accountSettingsService.DELIVERY_APPOINTMENT_REQUIRED ==
				JSON.parse(this.loadDetails.controls['deliveryAppointment'].value)
		) {
			return 'false';
		} else {
			return 'true';
		}
	}

	updatePickupTime() {
		const DIFF_FROM_CURRENT_DATE = this.diffFromCurrentDate();
		if (DIFF_FROM_CURRENT_DATE == 1) {
			const openTime: string = this.getOpenTime();
			this.loadDetails.get('pickupOpenTime').setValue(openTime);
		} else {
			this.loadDetails.get('pickupOpenTime').setValue('08:00');
		}
	}

	getDistance() {
		const url: string = this.urlService.getDistanceInMiles();
		this.restService.POST(url, this.data.searchTruckForm).subscribe(
			(response) => {
				const distanceInMiles = response?.data;
				if (distanceInMiles) {
					if (distanceInMiles <= environment.MINIMUM_MILES_PER_DAY) {
						this.transitDays = Math.floor(distanceInMiles / environment.MILES_PER_DAY);
					} else {
						this.transitDays = Math.ceil(distanceInMiles / environment.MILES_PER_DAY);
					}
				} else {
					this.transitDays = 0;
				}
				this.minDeliveryDate = new Date(ADD_DAYS(this.pickupDate['value'], this.transitDays));
				this.estimatedDeliveryDate = new UntypedFormControl(
					new Date(DATE_TIME(this.minDeliveryDate))
				);
			},
			(error) => {
				this.transitDays = 0;
				this.utilService.showSnackbar(
					'Unable to find distance between origin and destination.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	openCarrierFilter() {
		this.switchToCarrierFilter.emit(this.loadDetails);
	}

	checkPickupLeadTime() {
		const DIFF_FROM_CURRENT_DATE = this.diffFromCurrentDate();
		if (DIFF_FROM_CURRENT_DATE > Number(this.bookingAssistantSettings.pickupDateThreshold)) {
			this.pickupDateLeadTimeData.isBeyondLimit = true;
		} else {
			this.pickupDateLeadTimeData.isBeyondLimit = false;
		}
	}

	diffFromCurrentDate(): number {
		return DIFFERENCE_IN_DAYS(DATE_YMD(TODAY()), DATE_YMD(this.pickupDate['value']));
	}

	setCustomer(customer: CustomerDTO) {
		this.selectedCustomer = customer;
	}

	async getPricingFieldFromBAConfig() {
		const enabledIntegrationFieldsResponse = await this.restService
			.GET(this.urlService.getEnabledIntegrationFields())
			.toPromise();
		const enabledIntegrationDataFields = JSON.parse(enabledIntegrationFieldsResponse.data.data);
		if (
			IS_NULL_OR_UNDEFINED(enabledIntegrationDataFields) ||
			(typeof enabledIntegrationDataFields === 'string' && IS_EMPTY(enabledIntegrationDataFields))
		) {
			// If no BA config, fall back to offerRate if available
			this.setOfferRateAsFallback();
			return;
		}
		this.baConfig.isLoadPricingSupported = Boolean(
			JSON.parse(enabledIntegrationDataFields['loadPricing']['isLoadPricingSupported'])
		);
		if (!this.baConfig.isLoadPricingSupported) {
			// If load pricing not supported, fall back to offerRate if available
			this.setOfferRateAsFallback();
			return;
		}

		const baConfigResponse = await this.restService
			.GET(this.urlService.getBookingAutomationConfigurationData())
			.toPromise();
		const baConfig = JSON.parse(baConfigResponse.data.data);
		this.baConfig.primaryPricingField = baConfig.primaryPricingField;
		this.baConfig.secondaryPricingField = baConfig.secondaryPricingField;
		let desiredPrice = null,
			primaryPricingField = (baConfig.primaryPricingField || '').toLowerCase();

		// Caller (e.g. v2 projects list) sets fetchPricing when the page has no pricing component to reuse.
		if (this.data?.fetchPricing === true) {
			if (primaryPricingField.indexOf('c4_rate') !== -1) {
				await this.fetchLanePricing();
			} else if (primaryPricingField.indexOf('green_screens') !== -1) {
				await this.fetchGreenScreensRate();
			}
		}

		const greenScreenTargetBuyRate = this.data?.greenScreenRate?.targetBuyRate;

		if (
			primaryPricingField.indexOf('c4_rate') !== -1 &&
			IS_NOT_NULL_OR_UNDEFINED(this.data?.lanePricing)
		) {
			if (primaryPricingField.indexOf('C4_RATE_LOWER_BOUND'.toLowerCase()) !== -1) {
				desiredPrice = this.data.lanePricing.allInCostLow;
			} else if (primaryPricingField.indexOf('C4_RATE_UPPER_BOUND'.toLowerCase()) !== -1) {
				desiredPrice = this.data.lanePricing.allInCostHigh;
			} else {
				desiredPrice = this.data.lanePricing.allInCost;
			}
		} else if (primaryPricingField.indexOf('green_screens') !== -1) {
			desiredPrice = greenScreenTargetBuyRate;
		}

		if (desiredPrice) {
			desiredPrice = Math.round(desiredPrice);
		}

		if (IS_NULL_OR_UNDEFINED(desiredPrice) && IS_NOT_NULL_OR_UNDEFINED(this.data?.offerRate)) {
			desiredPrice = Math.round(this.data.offerRate);
		}

		this.loadDetails.get('desiredPrice').setValue(desiredPrice);
	}

	private async fetchLanePricing() {
		if (IS_NULL_OR_UNDEFINED(this.data?.searchTruckForm)) return;
		try {
			const response = await this.restService
				.POST(this.urlService.getLanePricing(), this.data.searchTruckForm)
				.toPromise();
			if (IS_NOT_NULL_OR_UNDEFINED(response?.data)) {
				this.data.lanePricing = response.data;
			}
		} catch {}
	}

	private async fetchGreenScreensRate() {
		if (IS_NULL_OR_UNDEFINED(this.data?.searchTruckForm)) return;
		try {
			const searchForm = {
				...this.data.searchTruckForm,
				trailerType: this.data.searchTruckForm.trailerType?.split(',')[0],
			};
			const response = await this.restService
				.POST(this.urlService.getGreenScreensLanePricing(), searchForm)
				.toPromise();
			if (IS_NOT_NULL_OR_UNDEFINED(response?.data)) {
				this.data.greenScreenRate = response.data;
			}
		} catch {}
	}

	/**
	 * Sets the offerRate as the desired price if available.
	 * Used as a fallback when BA config is not available or load pricing is not supported.
	 */
	private setOfferRateAsFallback() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.data?.offerRate)) {
			this.loadDetails.get('desiredPrice').setValue(Math.round(this.data.offerRate));
		}
	}

	// TODO: update equipment/trailer type handling to have consistent naming.
	// c4 needs POWER ONLY as POWERONLY to be able to deserialize the request. Remove this in https://cargochief.atlassian.net/browse/CN-6553
	formatPowerOnlyTrailerType(trailerType) {
		if (trailerType === 'POWER ONLY') {
			return 'POWERONLY';
		} else {
			return trailerType;
		}
	}

	// Anything JSON.stringify turns into null has to be screened here: NaN and +/-Infinity both pass
	// a != null check and serialize as null, which is how a quote with no declared value used to
	// reach the server as an explicit null. Blank and whitespace-only are not values either.
	private hasNumericValue(value: any): boolean {
		if (IS_NULL_OR_UNDEFINED(value) || String(value).trim() === '') {
			return false;
		}
		return Number.isFinite(Number(value));
	}
}
